import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { AttachmentViewer, type AttachmentViewerSubject } from '@/components/attachments/AttachmentViewer';
import {
  Button,
  EmptyState,
  ListPager,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EmailLink } from '@/components/ui/TextLink';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import type { EmailThread, Paginated } from '@/lib/types';

/**
 * The company's logged email threads (the BCC email log, 2026-10-23), newest
 * first as the server orders them. One row per thread; "Open trail" opens the
 * thread's trail HTML — a company attachment — in the same in-app
 * `AttachmentViewer`, loaded through the same `api.downloadCompanyAttachment`
 * the Attachments panel uses. Nothing here re-implements the viewer.
 *
 * - Rendered bare (no outer `Panel`): the detail page owns the section chrome.
 *   `ListPager` is a `PanelFooter` and sits at the bottom.
 * - `trailAttachmentId` is `null` until the thread has a company; on this list
 *   the thread is on this company, but a missing trail still offers no control
 *   rather than a dead one.
 * - Show all asks for one page of 100 (the server cap); beyond that the pager
 *   says "Showing the first 100 of N".
 * - Every address is an `EmailLink` (owner, 2026-10-16).
 */

const PER_PAGE = 25;
const SHOW_ALL_PER_PAGE = 100;

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; answer: Paginated<EmailThread> };

function messageOf(caught: unknown, fallback: string): string {
  return caught instanceof Error && caught.message !== '' ? caught.message : fallback;
}

function Participants({ thread }: { thread: EmailThread }): ReactNode {
  if (thread.participants.length === 0) return <span className="text-fg-muted">—</span>;
  return (
    <span className="flex min-w-0 flex-wrap gap-x-2 gap-y-0.5">
      {thread.participants.map((person) => (
        <EmailLink key={person.address} address={person.address} title={person.address}>
          {person.name !== null && person.name !== '' ? person.name : person.address}
        </EmailLink>
      ))}
    </span>
  );
}

function LastSender({ thread }: { thread: EmailThread }): ReactNode {
  const sender = thread.lastSender;
  if (sender === null) return <span className="text-fg-muted">—</span>;
  return (
    <EmailLink address={sender.address} title={sender.address}>
      {sender.name !== null && sender.name !== '' ? sender.name : sender.address}
    </EmailLink>
  );
}

function LastMessageAt({ thread }: { thread: EmailThread }): ReactNode {
  return thread.lastMessageAt === null ? (
    <span className="text-fg-muted">—</span>
  ) : (
    <span className="tabular-nums">{formatDateTime(thread.lastMessageAt)}</span>
  );
}

function subjectOf(thread: EmailThread): string {
  return thread.subject !== '' ? thread.subject : '(no subject)';
}

export function CompanyEmailTrails({ companyId, companyName }: { companyId: number; companyName: string }) {
  const [page, setPage] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  /*
   * Keyed by the request it answers, so "refreshing" is derived (the shown
   * answer is for another key) rather than a flag set inside the effect.
   */
  const [result, setResult] = useState<{ key: string; companyId: number; state: LoadState } | null>(null);
  /* Held in state so the viewer's load effect sees a stable subject. */
  const [viewing, setViewing] = useState<AttachmentViewerSubject | null>(null);

  /* A different company starts again from page 1. */
  const [shownFor, setShownFor] = useState(companyId);
  if (shownFor !== companyId) {
    setShownFor(companyId);
    setPage(1);
    setShowAll(false);
  }

  const key = `${companyId}:${showAll ? 'all' : page}:${reloadToken}`;
  /* Another company's answer is never shown, not even dimmed. */
  const state: LoadState =
    result === null || result.companyId !== companyId ? { status: 'loading' } : result.state;
  const refreshing = result !== null && result.companyId === companyId && result.key !== key;

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    api
      .listEmailThreads(
        showAll
          ? { companyId, page: 1, perPage: SHOW_ALL_PER_PAGE }
          : { companyId, page, perPage: PER_PAGE },
        signal,
      )
      .then((answer) => {
        if (signal.aborted) return;
        setResult({ key, companyId, state: { status: 'ready', answer } });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setResult({
          key,
          companyId,
          state: { status: 'error', message: messageOf(caught, 'The email trails couldn’t be loaded.') },
        });
      });
    return () => controller.abort();
  }, [companyId, page, showAll, key]);

  const openTrail = useCallback(
    (thread: EmailThread) => {
      const attachmentId = thread.trailAttachmentId;
      if (attachmentId === null) return;
      setViewing({
        kind: 'file',
        title: `Email trail · ${subjectOf(thread)}`,
        fileName: null,
        contentType: 'text/html',
        load: (signal) => api.downloadCompanyAttachment(companyId, attachmentId, signal),
      });
    },
    [companyId],
  );

  const openButton = (thread: EmailThread) =>
    thread.trailAttachmentId === null ? null : (
      <Button variant="secondary" size="sm" className="max-sm:h-11" onClick={() => openTrail(thread)}>
        Open trail
      </Button>
    );

  let body: ReactNode;
  if (state.status === 'loading' || (state.status === 'error' && refreshing)) {
    body = (
      <div className="grid gap-2 p-4" aria-busy="true">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-9 w-full" />
        ))}
      </div>
    );
  } else if (state.status === 'error') {
    body = (
      <div className="p-4">
        <Notice
          tone="danger"
          action={
            <Button variant="secondary" size="sm" onClick={() => setReloadToken((value) => value + 1)}>
              Retry
            </Button>
          }
        >
          {state.message}
        </Notice>
      </div>
    );
  } else if (state.answer.data.length === 0) {
    body = (
      <EmptyState
        title="No emails logged yet."
        description={
          <>
            BCC <EmailLink address="emerge@seedglobaleducation.com" /> on emails to this school.
          </>
        }
      />
    );
  } else {
    const threads = state.answer.data;
    body = (
      <div className={refreshing ? 'opacity-70' : undefined} aria-busy={refreshing || undefined}>
        <div className="max-md:hidden">
          <TableWrap>
            <Table aria-label={`Email trails for ${companyName}`}>
              <THead>
                <TR>
                  <TH className="min-w-56">Subject</TH>
                  <TH className="min-w-56">Participants</TH>
                  <TH numeric>Messages</TH>
                  <TH className="min-w-40">Last message</TH>
                  <TH className="min-w-40">Last sender</TH>
                  <TH numeric>Files</TH>
                  <TH>
                    <span className="sr-only">Actions</span>
                  </TH>
                </TR>
              </THead>
              <TBody>
                {threads.map((thread) => (
                  <TR key={thread.id}>
                    <TD className="max-w-80">
                      <span className="font-medium text-fg [overflow-wrap:anywhere]">{subjectOf(thread)}</span>
                    </TD>
                    <TD className="max-w-80">
                      <Participants thread={thread} />
                    </TD>
                    <TD numeric className="tabular-nums">
                      {thread.messageCount}
                    </TD>
                    <TD>
                      <LastMessageAt thread={thread} />
                    </TD>
                    <TD className="max-w-60">
                      <LastSender thread={thread} />
                    </TD>
                    <TD numeric className="tabular-nums">
                      {thread.fileCount}
                    </TD>
                    <TD className="text-right">{openButton(thread)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableWrap>
        </div>

        <ul className="m-0 list-none p-0 md:hidden">
          {threads.map((thread) => (
            <li key={thread.id} className="border-b border-border px-4 py-3 last:border-b-0">
              <p className="m-0 text-sm font-semibold text-fg [overflow-wrap:anywhere]">{subjectOf(thread)}</p>
              <dl className="m-0 mt-2 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
                <dt className="text-fg-muted">Participants</dt>
                <dd className="m-0 min-w-0">
                  <Participants thread={thread} />
                </dd>
                <dt className="text-fg-muted">Messages</dt>
                <dd className="m-0 tabular-nums">{thread.messageCount}</dd>
                <dt className="text-fg-muted">Last message</dt>
                <dd className="m-0">
                  <LastMessageAt thread={thread} />
                </dd>
                <dt className="text-fg-muted">Last sender</dt>
                <dd className="m-0 min-w-0">
                  <LastSender thread={thread} />
                </dd>
                <dt className="text-fg-muted">Files</dt>
                <dd className="m-0 tabular-nums">{thread.fileCount}</dd>
              </dl>
              {thread.trailAttachmentId !== null && <div className="mt-2">{openButton(thread)}</div>}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const answer = state.status === 'ready' ? state.answer : null;
  const shownPerPage = showAll ? SHOW_ALL_PER_PAGE : PER_PAGE;
  const firstRow = answer === null || answer.data.length === 0 ? 0 : (answer.meta.page - 1) * shownPerPage + 1;
  const lastRow = answer === null || firstRow === 0 ? 0 : firstRow + answer.data.length - 1;

  return (
    <div className="min-w-0">
      {body}
      {answer !== null && answer.meta.total > PER_PAGE && (
        <ListPager
          page={answer.meta.page}
          totalPages={answer.meta.totalPages}
          total={answer.meta.total}
          firstRow={firstRow}
          lastRow={lastRow}
          perPage={PER_PAGE}
          showAll={showAll}
          loading={refreshing}
          onPage={(next) => setPage(next)}
          onToggleShowAll={(next) => {
            setShowAll(next);
            setPage(1);
          }}
          noun="threads"
          truncated={showAll && answer.meta.total > answer.data.length}
        />
      )}
      <AttachmentViewer subject={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
